import React, { useState, useEffect, useRef } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { useForm } from 'react-hook-form';
import { translateData, getLanguages, getCrops } from '../lib/api';
import { RichTextEditor } from './child/RichTextEditor';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast } from 'react-toastify';

const customStyles = `
  .form-grid-container {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 1.5rem !important;
    width: 100% !important;
    margin-bottom: 1.25rem !important;
  }
  .form-control, textarea {
    width: 100% !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
  }
  .error-message { color: #dc3545; font-size: 0.75rem; }
  .success-message { color: #28a745; text-align: center; margin-bottom: 1rem; }

  /* PDF Upload Styles */
  .pdf-upload {
    border: 2px dashed #e2e8f0 !important;
    border-radius: 8px !important;
    padding: 1rem !important;
    text-align: center !important;
    background: #f8fafc !important;
    cursor: pointer !important;
    min-height: 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .pdf-upload:hover {
    background: #f1f5f9 !important;
  }

  .upload-icon {
    font-size: 20px;
    color: #64748b;
  }

  .upload-text {
    color: #64748b;
    font-size: 0.875rem;
  }

  .pdf-preview {
    margin-top: 10px;
    padding: 8px;
    background: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }

  .pdf-file-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
  }

  .remove-pdf-btn {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 2px 8px;
    font-size: 1.2rem;
  }

  .remove-pdf-btn:hover {
    color: #c82333;
  }

  /* Main Tabs */
  .main-tabs { 
    display: flex; 
    gap: 8px; 
    margin-bottom: 12px; 
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 12px;
  }
  .main-tab {
    padding: 8px 14px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
  }
  .main-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }

  /* Language Sub-Tabs */
  .lang-tabs { 
    display: flex; 
    gap: 8px; 
    flex-wrap: wrap; 
    margin-bottom: 20px;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 12px;
  }
  .lang-tab {
    padding: 6px 12px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    font-size: 0.875rem;
  }
  .lang-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }

  .drawer-content-scroll {
    height: calc(100vh - 40px);
    overflow-y: auto !important;
    padding-right: 10px;
  }

  .file-size {
    font-size: 0.75rem;
    color: #6b7280;
  }
  
  .form-label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #374151;
  }
  
  .mb-20 {
    margin-bottom: 20px;
  }
  
  .text-xxl {
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  .text-primary-light {
    color: #1a73e8;
  }
`;

export default function AddCropWiseCommonInfo({ isOpen, onClose, onSubmit, topicToEdit }) {
    const [message, setMessage] = useState(null);
    const [step, setStep] = useState('basic'); // 'basic' | 'translate' | 'translations'
    const [mainTab, setMainTab] = useState('english'); // 'english' | 'other'
    const [languages, setLanguages] = useState([]);
    const [crops, setCrops] = useState([]);
    const [topicContents, setTopicContents] = useState({});
    const [pdfFiles, setPdfFiles] = useState({}); // {languageCode: file}
    const [existingPdfs, setExistingPdfs] = useState({}); // {languageCode: filename}
    const [loadingLanguages, setLoadingLanguages] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [videoUrls, setVideoUrls] = useState({}); // {languageCode: url}
    const [videoUrlsSelected, setVideoUrlsSelected] = useState({}); // {languageCode: boolean} - checkboxes for saving
    const [sameVideoForAllLanguages, setSameVideoForAllLanguages] = useState(false);

    // Translations state
    const [translations, setTranslations] = useState({
        TopicName: {}
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        getValues,
        watch,
        trigger,
        setError
    } = useForm({
        defaultValues: {
            topicName: topicToEdit?.TopicName || '',
            cropId: topicToEdit?.CropID || '',
            priority: topicToEdit?.Priority || 0,
            active: topicToEdit?.IsActive !== undefined ? Boolean(topicToEdit.IsActive) : true,
        },
    });

    const selectedCropId = watch('cropId');

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen]);

    // Load crops and languages
    useEffect(() => {
        const loadData = async () => {
            if (!isOpen) return;

            try {
                const token = localStorage.getItem('authToken') || localStorage.getItem('token');

                // Crops
                const cropsRes = await getCrops(token);
                if (cropsRes) {
                    const sortedCrops = (cropsRes || []).sort((a, b) =>
                        a.CropName?.localeCompare(b.CropName)
                    );
                    setCrops(sortedCrops);
                } else if (cropsRes?.data) {
                    setCrops(cropsRes.data);
                }

                // Languages
                setLoadingLanguages(true);
                const languagesRes = await getLanguages();
                let languagesList = [];
                if (languagesRes?.status === 'success' && languagesRes?.data) {
                    languagesList = languagesRes.data;
                } else if (Array.isArray(languagesRes?.data)) {
                    languagesList = languagesRes.data;
                } else if (Array.isArray(languagesRes)) {
                    languagesList = languagesRes;
                }

                const activeLanguages = languagesList.filter(lang =>
                    lang.IsLanguageActivated === true || lang.IsLanguageActivated === 1 ||
                    lang.IsActive === true || lang.IsActive === 1 ||
                    lang.IsActivated === true || lang.IsActivated === 1
                );

                setLanguages(activeLanguages);

                // Set default selected language
                if (activeLanguages.length > 0) {
                    const nonEnglish = activeLanguages.find(lang =>
                        (lang.GoogleLanguageCode !== 'en' && lang.GoogleLanguageCode !== 'en-US')
                    );
                    if (nonEnglish) {
                        setSelectedLanguage(nonEnglish.GoogleLanguageCode || nonEnglish.LanguageCode);
                    }
                }

            } catch (error) {
                console.error('Failed to load data:', error);
                toast(`Failed to load data: ${error.message}`);
            } finally {
                setLoadingLanguages(false);
            }
        };

        loadData();
    }, [isOpen]);

    // Prefill data for edit mode
    useEffect(() => {
        if (!isOpen) return;

        setPdfFiles({});
        setExistingPdfs({});
        setMessage(null);
        setStep('basic');
        setMainTab('english');
        setVideoUrls({});
        setVideoUrlsSelected({}); // Reset video URL checkboxes - always unchecked by default
        setSameVideoForAllLanguages(false);

        if (topicToEdit) {
            // Set Form Values
            setValue('topicName', topicToEdit.TopicName || '');
            setValue('cropId', topicToEdit.CropID || '');
            setValue('priority', topicToEdit.Priority || 0);
            setValue('active', Boolean(topicToEdit.IsActive));

            // Parse Translations
            let tName = {};
            try {
                const parsed = topicToEdit.TopicNameOtherLang;
                if (typeof parsed === 'string') tName = JSON.parse(parsed);
                else if (typeof parsed === 'object') tName = parsed;
                if (tName.TopicName) tName = tName.TopicName;
            } catch (e) { console.warn(e); }

            const translationsObj = { TopicName: {} };
            languages.forEach(lang => {
                const langCode = lang.GoogleLanguageCode || lang.LanguageCode;
                translationsObj.TopicName[langCode] = tName[langCode] || '';
            });
            setTranslations(translationsObj);

            // Parse Contents, Existing PDFs & Video URLs
            const topicContentsObj = {};
            const existingPdfsObj = {};
            const videoUrlsObj = {};

            if (topicToEdit.CropWiseCommonInfoDetail && Array.isArray(topicToEdit.CropWiseCommonInfoDetail)) {
                topicToEdit.CropWiseCommonInfoDetail.forEach(detail => {
                    const langCode = detail.GoogleLanguageCode || detail.LanguageCode;
                    if (langCode) {
                        topicContentsObj[langCode] = detail.TopicContent || '';
                        if (detail.UploadedFileName) {
                            existingPdfsObj[langCode] = detail.UploadedFileName;
                        }
                        // Load video URLs from the detail
                        if (detail.VideoURL) {
                            videoUrlsObj[langCode] = detail.VideoURL;
                        }
                    }
                });
            }

            setTopicContents(topicContentsObj);
            setExistingPdfs(existingPdfsObj);
            setVideoUrls(videoUrlsObj);
        } else {
            // Reset for New Entry
            reset({ topicName: '', cropId: '', priority: 0, active: true });
            setTranslations({ TopicName: {} });
            setTopicContents({});
        }
    }, [topicToEdit, isOpen, languages]);

    const callTranslateApi = async ({ TopicName, TopicContent }) => {
        try {
            const res = await translateData({
                TopicName,
                TopicContent
            });

            console.log('Translate API response:', res);

            const maybeData = res?.data ?? res ?? {};
            const data = maybeData?.data && (maybeData.TopicName === undefined)
                ? maybeData.data
                : maybeData;

            const normalized = { TopicName: {}, TopicContent: {} };

            if (languages.length > 0) {
                languages.forEach(lang => {
                    const langCode = lang.GoogleLanguageCode || lang.LanguageCode;
                    normalized.TopicName[langCode] =
                        data.TopicName?.[langCode] || data.TopicName?.en || TopicName;

                    normalized.TopicContent[langCode] =
                        data.TopicContent?.[langCode] || data.TopicContent?.en || TopicContent;
                });
            } else {
                normalized.TopicName.en = data?.TopicName?.en || TopicName;
                normalized.TopicContent.en = data?.TopicContent?.en || TopicContent;
            }

            return normalized;
        } catch (err) {
            console.error('Translate API error: ', err);

            const fallback = { TopicName: {}, TopicContent: {} };
            if (languages.length > 0) {
                languages.forEach(lang => {
                    const langCode = lang.GoogleLanguageCode || lang.LanguageCode;
                    fallback.TopicName[langCode] = TopicName;
                    fallback.TopicContent[langCode] = TopicContent;
                });
            } else {
                fallback.TopicName.en = TopicName;
                fallback.TopicContent.en = TopicContent;
            }
            return fallback;
        }
    };

    const onInitialTranslate = async (data) => {
        // Validate form
        const isValid = await trigger(['topicName', 'cropId']);
        if (!isValid) {
            toast('Please fill all required fields correctly');
            return;
        }

        if (languages.length === 0) {
            toast('Languages not loaded yet. Please try again.');
            return;
        }

        if (!data.cropId) {
            toast('Please select a crop');
            return;
        }

        // Get English content
        const englishLang = getEnglishLanguage();
        const englishLangCode = getLanguageCode(englishLang);
        const englishContent = topicContents[englishLangCode] || '';

        if (!englishContent.trim()) {
            toast('Please enter topic content in English');
            return;
        }

        setStep('translate');

        const translated = await callTranslateApi({
            TopicName: data.topicName,
            TopicContent: englishContent
        });

        console.log('Translated data:', translated);

        // Update topic name translations
        setTranslations(prev => ({
            TopicName: { ...prev.TopicName, ...translated.TopicName }
        }));

        // Update topic contents for all languages
        const updatedContents = { ...topicContents };
        languages.forEach(lang => {
            const langCode = lang.GoogleLanguageCode || lang.LanguageCode;
            if (translated.TopicContent[langCode]) {
                updatedContents[langCode] = translated.TopicContent[langCode];
            }
        });
        setTopicContents(updatedContents);

        setStep('translations');
        setMainTab('other');
        setMessage(null);
    };

    const onFinalSave = async () => {
        const topicName = getValues('topicName');
        const cropId = getValues('cropId');
        const priority = getValues('priority');
        const active = getValues('active');

        if (!topicName.trim() || !cropId) {
            setMessage('Please fill all required fields');
            return;
        }

        // Validate VideoURLs
        for (const [langCode, url] of Object.entries(videoUrls)) {
            if (url && !isValidUrl(url)) {
                setMessage(`Invalid video URL format for language: ${langCode}`);
                return;
            }
        }

        const missingContent = languages.filter(lang => {
            const langCode = getLanguageCode(lang);
            return !topicContents[langCode]?.trim();
        });

        if (missingContent.length > 0) {
            const missingNames = missingContent.map(l => l.LanguageName).join(', ');
            setMessage(`Please enter topic content for: ${missingNames}`);
            return;
        }

        // Prepare CropWiseCommonInfoDetail array
        const cropWiseCommonInfoDetail = languages.map(lang => {
            const langCode = getLanguageCode(lang);
            const langId = lang.ID || lang.LanguageID;

            const newFile = pdfFiles[langCode];     // File Object or undefined
            const existingFile = existingPdfs[langCode]; // String filename or undefined

            let fileNamePayload = null;
            if (newFile) {
                fileNamePayload = newFile.name; // Send string name in JSON, Binary goes via 'pdfFiles'
            } else if (existingFile) {
                fileNamePayload = existingFile;
            }

            return {
                LanguageID: langId,
                LanguageCode: lang.LanguageCode,
                GoogleLanguageCode: langCode,
                TopicContent: topicContents[langCode] || '',
                VideoURL: videoUrls[langCode] || null, // Always include video URL if provided
                UploadedFileName: fileNamePayload // Ensure this is a string, not a File object
            };
        });

        // Get the VideoURL - use English VideoURL as the master VideoURL
        const englishLang = getEnglishLanguage();
        const englishLangCode = getLanguageCode(englishLang);
        const videoURL = videoUrls[englishLangCode] || null;

        const payload = {
            CropID: cropId,
            TopicName: topicName,
            Priority: parseInt(priority),
            IsActive: active,
            VideoURL: videoURL,
            TopicNameOtherLang: JSON.stringify({ TopicName: translations.TopicName }),
            CropWiseCommonInfoDetail: JSON.stringify(cropWiseCommonInfoDetail),
            ...(topicToEdit && topicToEdit.ID ? { ID: topicToEdit.ID } : {})
        };

        console.log("Final payload:", payload);
        console.log("PDF files to upload:", pdfFiles);

        try {
            await onSubmit(payload, !!topicToEdit, pdfFiles);

            setMessage({
                type: 'success',
                text: topicToEdit ? 'Crop wise common info updated successfully!' : 'Crop wise common info added successfully!'
            });

            setPdfFiles({});
            setExistingPdfs({});

            setTimeout(() => {
                onClose();
                setMessage(null);
            }, 1200);
        } catch (error) {
            const errorMsg = error?.response?.data?.message || error?.message || 'Failed to save crop wise common info';
            if (errorMsg.toLowerCase().includes('priority')) {
                setError('priority', {
                    type: 'manual',
                    message: errorMsg
                });
            }
            toast(errorMsg);
        }
    };

    const handleTopicContentChange = (languageCode, content) => {
        setTopicContents(prev => ({
            ...prev,
            [languageCode]: content
        }));
    };

    const handlePdfUpload = (languageCode, file) => {
        if (file) {
            // Validate file type
            const validTypes = ['application/pdf', '.pdf'];
            const isValidType = validTypes.some(type =>
                file.type.includes(type) || file.name.toLowerCase().endsWith(type)
            );

            if (!isValidType) {
                toast('Please upload a PDF file');
                return;
            }

            // Validate file size (10MB max)
            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                toast('PDF size should be less than 10MB');
                return;
            }

            setPdfFiles(prev => ({
                ...prev,
                [languageCode]: file
            }));
            setMessage(null);
        }
    };

    const handleRemovePdf = (languageCode) => {
        setPdfFiles(prev => {
            const newFiles = { ...prev };
            delete newFiles[languageCode];
            return newFiles;
        });

        setExistingPdfs(prev => {
            const newExisting = { ...prev };
            delete newExisting[languageCode];
            return newExisting;
        });
    };

    const handleVideoUrlChange = (languageCode, url) => {
        setVideoUrls(prev => ({
            ...prev,
            [languageCode]: url
        }));
    };

    const handleVideoUrlSelectChange = (languageCode, checked) => {
        setVideoUrlsSelected(prev => ({
            ...prev,
            [languageCode]: checked
        }));
    };

    const handleSameVideoForAllLanguages = (checked) => {
        setSameVideoForAllLanguages(checked);

        if (checked) {
            // Copy English VideoURL to all languages
            const englishLang = getEnglishLanguage();
            const englishLangCode = getLanguageCode(englishLang);
            const englishVideoUrl = videoUrls[englishLangCode] || '';

            const updatedVideoUrls = { ...videoUrls };
            languages.forEach(lang => {
                const langCode = getLanguageCode(lang);
                updatedVideoUrls[langCode] = englishVideoUrl;
            });
            setVideoUrls(updatedVideoUrls);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const isValidUrl = (urlString) => {
        if (!urlString || !urlString.trim()) {
            return true; // Empty is valid since it's optional
        }
        try {
            new URL(urlString);
            return true;
        } catch (e) {
            return false;
        }
    };

    const getLanguageCode = (lang) => {
        if (!lang) return '';
        return lang.GoogleLanguageCode || lang.LanguageCode || '';
    };

    const getEnglishLanguage = () => {
        return languages.find(lang => {
            const langCode = getLanguageCode(lang);
            return langCode === 'en' || langCode === 'en-US' || langCode === 'en-GB';
        }) || languages[0]; // Fallback to first language
    };

    const getSelectedLanguageObj = () => {
        return languages.find(lang => getLanguageCode(lang) === selectedLanguage) || {};
    };

    const renderEnglishTab = () => {
        const englishLang = getEnglishLanguage();
        const englishLangCode = getLanguageCode(englishLang);

        return (
            <form onSubmit={handleSubmit(onInitialTranslate)}>
                {/* Crop Dropdown */}
                <div className="form-grid-container">
                    <div style={{ gridColumn: 'span 2' }}>
                        <label className="form-label">Crop *</label>
                        <select
                            className="form-control"
                            {...register('cropId', { required: 'Please select a crop' })}
                        >
                            <option value="">Select Crop</option>
                            {crops.map(crop => (
                                <option key={crop.ID} value={crop.ID}>
                                    {crop.CropName}
                                </option>
                            ))}
                        </select>
                        {errors.cropId && <span className="error-message">{errors.cropId.message}</span>}
                    </div>
                </div>

                {/* Topic Name */}
                <div className="form-grid-container">
                    <div style={{ gridColumn: 'span 2' }}>
                        <label className="form-label">Topic Name *</label>
                        <input
                            className="form-control"
                            {...register('topicName', {
                                required: 'Topic Name is required',
                                minLength: { value: 2, message: 'Minimum 2 characters' }
                            })}
                            placeholder="Enter topic name"
                        />
                        {errors.topicName && <span className="error-message">{errors.topicName.message}</span>}
                    </div>
                </div>

                {/* English Topic Content */}
                <div className="mb-20">
                    <label className="form-label">Topic Content ({englishLang?.LanguageName || 'English'}) *</label>
                    <RichTextEditor
                        value={topicContents[englishLangCode] || ''}
                        onChange={(content) => handleTopicContentChange(englishLangCode, content)}
                        placeholder={`Enter topic content in ${englishLang?.LanguageName || 'English'}`}
                    />
                    {!topicContents[englishLangCode]?.trim() && (
                        <span className="error-message">Topic content is required</span>
                    )}
                </div>

                {/* English PDF Upload */}
                <div className="mb-20">
                    <label className="form-label">PDF for {englishLang?.LanguageName || 'English'} (Optional)</label>
                    {existingPdfs[englishLangCode] ? (
                        <div className="pdf-preview">
                            <div className="pdf-file-info">
                                <span>📄 {existingPdfs[englishLangCode]}</span>
                                <button
                                    type="button"
                                    className="remove-pdf-btn"
                                    onClick={() => handleRemovePdf(englishLangCode)}
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    ) : pdfFiles[englishLangCode] ? (
                        <div className="pdf-preview">
                            <div className="pdf-file-info">
                                <span>📄 {pdfFiles[englishLangCode].name}</span>
                                <span className="file-size">({formatFileSize(pdfFiles[englishLangCode].size)})</span>
                                <button
                                    type="button"
                                    className="remove-pdf-btn"
                                    onClick={() => handleRemovePdf(englishLangCode)}
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div
                            className="pdf-upload"
                            onClick={() => document.getElementById(`pdf-${englishLangCode}`)?.click()}
                        >
                            <input
                                id={`pdf-${englishLangCode}`}
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={(e) => handlePdfUpload(englishLangCode, e.target.files[0])}
                                style={{ display: 'none' }}
                            />
                            <div className="upload-icon">📁</div>
                            <div className="upload-text">Click to upload PDF for {englishLang?.LanguageName || 'English'}</div>
                            <div className="upload-text" style={{ fontSize: '0.75rem' }}>
                                Supports: PDF (Max 10MB)
                            </div>
                        </div>
                    )}
                </div>

                <div className="form-grid-container">
                    <div style={{ gridColumn: 'span 2' }}>
                        <label className="form-label">Video URL (Optional)</label>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                            <input
                                className="form-control"
                                type="text"
                                value={videoUrls[englishLangCode] || ''}
                                onChange={(e) => handleVideoUrlChange(englishLangCode, e.target.value)}
                                placeholder="Enter video URL (e.g., https://www.youtube.com/watch?v=...)"
                                style={{ flex: 1 }}
                            />
                        </div>
                        {videoUrls[englishLangCode] && !isValidUrl(videoUrls[englishLangCode]) && (
                            <span className="error-message">Please enter a valid URL format</span>
                        )}
                    </div>
                </div>

                <div className="form-grid-container">
                    <div style={{ gridColumn: 'span 2' }}>
                        <div className="form-check style-check d-flex align-items-center gap-8">
                            <input
                                className="form-check-input radius-4 border border-neutral-400"
                                type="checkbox"
                                id="sameVideoCheckbox"
                                checked={sameVideoForAllLanguages}
                                onChange={(e) => handleSameVideoForAllLanguages(e.target.checked)}
                            />
                            <label className="form-label fw-medium text-primary-light text-sm mb-0" htmlFor="sameVideoCheckbox">
                                Same Video URL for all Languages
                            </label>
                        </div>
                    </div>
                </div>

                <div className="form-grid-container">
                    <div>
                        <label className="form-label">Priority *</label>
                        <input
                            type="number"
                            className="form-control"
                            {...register('priority', {
                                required: 'Priority is required',
                                valueAsNumber: true,
                                min: { value: 0, message: 'Priority must be 0 or greater' },
                                validate: {
                                    isInteger: (value) => Number.isInteger(value) || 'Priority must be an integer'
                                }
                            })}
                            placeholder="Enter priority (0, 1, 2, ...)"
                        />
                        {errors.priority && <span className="error-message">{errors.priority.message}</span>}
                    </div>

                    {/* Status */}
                    <div>
                        <label className="form-label">Status</label>
                        <div className="form-check style-check d-flex align-items-center gap-8">
                            <input
                                className="form-check-input radius-4 border border-neutral-400"
                                type="checkbox"
                                {...register('active')}
                            />
                            <label className="form-label fw-medium text-primary-light text-sm mb-0">
                                Active
                            </label>
                        </div>
                    </div>
                </div>

                {/* Buttons */}
                <div className="d-flex justify-content-center gap-3">
                    <button
                        type="button"
                        className="border border-danger-600 text-danger-600 px-56 py-10 radius-8"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-yellow px-56 py-12"
                        disabled={loadingLanguages || languages.length === 0}
                        style={{ opacity: (loadingLanguages || languages.length === 0) ? 0.5 : 1 }}
                    >
                        {loadingLanguages ? 'Loading...' : 'Translate'}
                    </button>
                </div>
            </form>
        );
    };

    const renderOtherLanguagesTab = () => {
        const englishLang = getEnglishLanguage();
        const englishLangCode = getLanguageCode(englishLang);
        const otherLanguages = languages.filter(lang =>
            getLanguageCode(lang) !== englishLangCode
        );
        const selectedLangObj = getSelectedLanguageObj();
        const selectedLangCode = getLanguageCode(selectedLangObj);
        const selectedLangName = selectedLangObj.LanguageName || selectedLangCode;

        if (otherLanguages.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <p>No other languages available</p>
                    <button
                        type="button"
                        className="btn btn-yellow px-56 py-12"
                        onClick={onFinalSave}
                    >
                        Save
                    </button>
                </div>
            );
        }

        return (
            <>
                {/* Language Sub-Tabs */}
                <div className="lang-tabs">
                    {otherLanguages.map(lang => {
                        const langCode = getLanguageCode(lang);
                        const langName = lang.LanguageName || langCode;
                        const isActive = selectedLanguage === langCode;

                        return (
                            <button
                                key={langCode}
                                type="button"
                                className={`lang-tab ${isActive ? 'active' : ''}`}
                                onClick={() => setSelectedLanguage(langCode)}
                            >
                                {langName} ({langCode})
                            </button>
                        );
                    })}
                </div>

                {/* Selected Language Content */}
                {selectedLanguage && (
                    <>
                        <div className="form-grid-container">
                            <div style={{ gridColumn: 'span 2' }}>
                                <label className="form-label">Topic Name ({selectedLangName}) *</label>
                                <input
                                    className="form-control"
                                    value={translations.TopicName[selectedLangCode] || ''}
                                    onChange={(e) =>
                                        setTranslations(prev => ({
                                            ...prev,
                                            TopicName: { ...prev.TopicName, [selectedLangCode]: e.target.value }
                                        }))
                                    }
                                    placeholder={`Enter topic name in ${selectedLangName}`}
                                />
                                {!translations.TopicName[selectedLangCode]?.trim() && (
                                    <span className="error-message">Topic name is required</span>
                                )}
                            </div>
                        </div>

                        <div className="mb-20">
                            <label className="form-label">Topic Content ({selectedLangName}) *</label>
                            <RichTextEditor
                                value={topicContents[selectedLangCode] || ''}
                                onChange={(content) => handleTopicContentChange(selectedLangCode, content)}
                                placeholder={`Enter topic content in ${selectedLangName}`}
                            />
                            {!topicContents[selectedLangCode]?.trim() && (
                                <span className="error-message">Topic content is required</span>
                            )}
                        </div>

                        <div className="mb-20">
                            <label className="form-label">PDF for {selectedLangName} (Optional)</label>
                            {existingPdfs[selectedLangCode] ? (
                                <div className="pdf-preview">
                                    <div className="pdf-file-info">
                                        <span>📄 {existingPdfs[selectedLangCode]}</span>
                                        <button
                                            type="button"
                                            className="remove-pdf-btn"
                                            onClick={() => handleRemovePdf(selectedLangCode)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ) : pdfFiles[selectedLangCode] ? (
                                <div className="pdf-preview">
                                    <div className="pdf-file-info">
                                        <span>📄 {pdfFiles[selectedLangCode].name}</span>
                                        <span className="file-size">({formatFileSize(pdfFiles[selectedLangCode].size)})</span>
                                        <button
                                            type="button"
                                            className="remove-pdf-btn"
                                            onClick={() => handleRemovePdf(selectedLangCode)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className="pdf-upload"
                                    onClick={() => document.getElementById(`pdf-${selectedLangCode}`)?.click()}
                                >
                                    <input
                                        id={`pdf-${selectedLangCode}`}
                                        type="file"
                                        accept=".pdf,application/pdf"
                                        onChange={(e) => handlePdfUpload(selectedLangCode, e.target.files[0])}
                                        style={{ display: 'none' }}
                                    />
                                    <div className="upload-icon">📁</div>
                                    <div className="upload-text">Click to upload PDF for {selectedLangName}</div>
                                    <div className="upload-text" style={{ fontSize: '0.75rem' }}>
                                        Supports: PDF (Max 10MB)
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="form-grid-container">
                            <div style={{ gridColumn: 'span 2' }}>
                                <label className="form-label">Video URL ({selectedLangName}) (Optional)</label>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                    <input
                                        className="form-control"
                                        type="text"
                                        value={videoUrls[selectedLangCode] || ''}
                                        onChange={(e) => handleVideoUrlChange(selectedLangCode, e.target.value)}
                                        placeholder={`Enter video URL for ${selectedLangName}`}
                                        style={{ flex: 1 }}
                                    />
                                </div>
                                {videoUrls[selectedLangCode] && !isValidUrl(videoUrls[selectedLangCode]) && (
                                    <span className="error-message">Please enter a valid URL format</span>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* Buttons */}
                <div className="d-flex justify-content-center gap-3">
                    <button
                        type="button"
                        className="border border-danger-600 text-danger-600 px-56 py-10 radius-8"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-yellow px-56 py-12"
                        onClick={onFinalSave}
                    >
                        Save
                    </button>
                </div>
            </>
        );
    };

    return (
        <>
            <style>{customStyles}</style>

            <Drawer open={isOpen} direction="right" size={900}>
                <button
                    variant="outline"
                    onClick={onClose}
                    style={{ border: 'none', color: '#000', padding: 0, textAlign: 'end', padding: '15px', position: 'absolute', top: 0, right: 0 }}
                >
                    <Icon icon="lucide:x" className="text-2xl" />
                </button>
                <div className="card-body drawer-content-scroll px-36 py-36">
                    <h6 className="text-xxl text-primary-light mb-16">
                        {topicToEdit ? 'Edit Crop Wise Common Info' : 'Add Crop Wise Common Info'}
                    </h6>

                    {message && (
                        <div className={message?.type === 'success' ? 'success-message' : 'error-message'}>
                            {message?.text || message}
                        </div>
                    )}

                    {/* Main Tabs: English / Other Languages */}
                    <div className="main-tabs">
                        <button
                            type="button"
                            className={`main-tab ${mainTab === 'english' ? 'active' : ''}`}
                            onClick={() => setMainTab('english')}
                        >
                            English
                        </button>
                        <button
                            type="button"
                            className={`main-tab ${mainTab === 'other' ? 'active' : ''}`}
                            onClick={() => setMainTab('other')}
                            disabled={step !== 'translations' && !topicToEdit}
                            title={step !== 'translations' && !topicToEdit ? 'Translate first to enable' : 'Other languages'}
                        >
                            Other Languages
                        </button>
                    </div>

                    {/* Loading state */}
                    {loadingLanguages ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <h4>Loading Languages…</h4>
                            <p>Please wait...</p>
                        </div>
                    ) : languages.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <h4>No Languages Found</h4>
                            <p>Please check if languages are activated in the system</p>
                        </div>
                    ) : (
                        <>
                            {/* Translating loader */}
                            {step === 'translate' && (
                                <div style={{ textAlign: 'center', padding: 40 }}>
                                    <h4>Translating…</h4>
                                    <p>Please wait...</p>
                                </div>
                            )}

                            {/* ENGLISH TAB */}
                            {mainTab === 'english' && step !== 'translate' && renderEnglishTab()}

                            {/* OTHER LANGUAGES TAB */}
                            {mainTab === 'other' && step !== 'translate' && renderOtherLanguagesTab()}
                        </>
                    )}
                </div>
            </Drawer>
        </>
    );
}